import React from 'react';

import { Money, formatMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import {
  Box,
  Button,
  Card,
  DropdownMenu,
  H2,
  HStack,
  Icon,
  Separator,
  ShimmerText,
  Text,
  TextColorToken,
  VStack,
} from '@betterment/samba-react';
import { CardLoader } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';
import { formatEnumLabel } from '@betterment/shared-components/src/utils/StringHelpers';

import { HouseholdOverviewDocument } from './graphql/query.householdOverview.generated';

import { TotalEarnedDateRange } from '../../../graphql/schema';

const TotalEarnings: React.FC<{
  totalEarnings?: Money;
  dateRangeOption: TotalEarnedDateRange;
  setDateRangeOption: (dateRangeOption: TotalEarnedDateRange) => void;
}> = ({ totalEarnings, dateRangeOption, setDateRangeOption }) => {
  const earnedColor: TextColorToken = totalEarnings && totalEarnings.cents > 0 ? 'text.success' : 'text';
  return (
    <VStack css={{ gap: 'space.50' }}>
      <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
        <Text variant="text100">Total earnings</Text>
        <DropdownMenu>
          <DropdownMenu.Trigger asChild={true}>
            <Button variant="textPrimary" css={{ alignItems: 'center', verticalAlign: 'middle' }}>
              <HStack>
                {formatEnumLabel(dateRangeOption).toLowerCase()}
                <Icon name="CaretDown" size="small" />
              </HStack>
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="center" side="bottom">
            {(Object.keys(TotalEarnedDateRange) as Array<keyof typeof TotalEarnedDateRange>).map(key => {
              const value = TotalEarnedDateRange[key];
              return (
                <DropdownMenu.Item
                  css={{
                    cursor: 'pointer',
                  }}
                  key={value}
                  onSelect={function Xs() {
                    setDateRangeOption(value);
                  }}
                >
                  {formatEnumLabel(value)}
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu>
      </HStack>

      {totalEarnings ? (
        <Text variant="text300" color={earnedColor}>
          {totalEarnings.cents > 0 ? '+' : ''}
          {formatMoney(totalEarnings)}
        </Text>
      ) : (
        <ShimmerText
          textStyle="text300"
          css={{
            maxWidth: '30%',
          }}
        />
      )}
    </VStack>
  );
};

export const HouseholdOverviewCard: React.FC = () => {
  const householdId = useRequiredParam('householdId');
  const [dateRangeOption, setDateRangeOption] = React.useState<TotalEarnedDateRange>(TotalEarnedDateRange.AllTime);
  const { data: newData, previousData } = useGraphQLQuery(HouseholdOverviewDocument, {
    variables: { id: householdId, dateRangeOption },
  });

  const data = newData ?? previousData;

  if (!data) return <CardLoader />;
  if (data.household.__typename === 'RecordNotFoundResult') return null;

  const totalEarnings = newData?.household?.__typename === 'Household' ? newData.household.totalEarnings : undefined;

  return (
    <Card>
      <Card.Header css={{ padding: 'space.300' }}>
        <H2 variant="title200">Performance</H2>
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content
        css={{
          paddingX: 'space.300',
          paddingTop: 'space.300',
          paddingBottom: 'space.400',
          display: 'flex',
          gap: 'space.300',
          flexDirection: 'column',
        }}
      >
        <Box css={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          <VStack css={{ gap: 'space.50' }}>
            <Text variant="text100">Total balance</Text>
            <Text variant="text300">{formatMoney(data.household.totalBalance)}</Text>
          </VStack>
          <VStack css={{ gap: 'space.50' }}>
            <Text variant="text100">External synced balance</Text>
            <Text variant="text300">{formatMoney(data.household.externalSyncedBalance)}</Text>
          </VStack>
        </Box>
        <Separator css={{ fullWidthWithInset: 'space.0' }} />
        <TotalEarnings
          totalEarnings={totalEarnings}
          dateRangeOption={dateRangeOption}
          setDateRangeOption={setDateRangeOption}
        />
      </Card.Content>
    </Card>
  );
};
